import { useEffect } from 'react'
import { useForm } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import ChangeContextFields from '@/frontend/features/Fde/ChangeContextFields'
import { FdeEditorDialog } from '@/frontend/features/Fde/FdeEditorSurface'
import { SelectControl, TextareaControl } from '@/frontend/features/Fde/FormControls'
import { fdeRoutes } from '@/frontend/features/Fde/routes'
import type { Evidence, FdeCopy, LinkType, ModelElement, Relationship } from '@/frontend/features/Fde/types'

interface Props { open: boolean; onOpenChange: (open: boolean) => void; relationship: Relationship | null; elements: ModelElement[]; linkTypes: LinkType[]; evidences: Evidence[]; projectId: string; copy: FdeCopy }

export default function RelationshipEditorDialog({ open, onOpenChange, relationship, elements, linkTypes, evidences, projectId, copy }: Props) {
  const form = useForm({ from_element_id: '', to_element_id: '', link_type_id: '', description: '', status: 'confirmado', change_note: '', evidence_id: '' })
  const choices = elements.map((element) => ({ value: element.id, label: `${element.name} · ${element.ontology_class?.name ?? element.kind}` }))
  const from = elements.find((element) => element.id === form.data.from_element_id)
  const to = elements.find((element) => element.id === form.data.to_element_id)
  const compatibleTypes = linkTypes.filter((linkType) => from?.ontology_class?.lineage.includes(linkType.from_class.key) && to?.ontology_class?.lineage.includes(linkType.to_class.key))
  const linkOptions = compatibleTypes.map((linkType) => ({ value: linkType.id, label: `${linkType.from_class.name} · ${linkType.forward_label} · ${linkType.to_class.name}` }))

  useEffect(() => {
    if (!open) return
    form.setData({
      from_element_id: relationship?.from.id ?? elements[0]?.id ?? '',
      to_element_id: relationship?.to.id ?? elements[1]?.id ?? elements[0]?.id ?? '',
      link_type_id: relationship?.link_type.id ?? '', description: relationship?.description ?? '',
      status: relationship?.status ?? 'confirmado', change_note: '', evidence_id: '',
    })
  }, [open, relationship?.id])

  useEffect(() => {
    if (!open || compatibleTypes.some((item) => item.id === form.data.link_type_id)) return
    form.setData('link_type_id', compatibleTypes[0]?.id ?? '')
  }, [open, form.data.from_element_id, form.data.to_element_id, linkTypes.length])

  function submit() {
    form.transform((data) => ({ relationship: data }))
    const options = { preserveScroll: true, onSuccess: () => onOpenChange(false) }
    const query = `?project_id=${projectId}`
    if (relationship) form.patch(`${fdeRoutes.relationships}/${relationship.id}${query}`, options)
    else form.post(`${fdeRoutes.relationships}${query}`, options)
  }

  return (
    <FdeEditorDialog
      open={open}
      onOpenChange={onOpenChange}
      title={relationship ? copy.workspace.edit_relationship : copy.workspace.add_relationship}
      closeLabel={copy.common.close}
      footer={<><Button variant="outline" onClick={() => onOpenChange(false)}>{copy.common.cancel}</Button><Button disabled={form.processing || !form.data.link_type_id} onClick={submit}>{copy.common.save}</Button></>}
    >
        <div className="grid gap-5 sm:grid-cols-2">
          <SelectControl label={copy.forms.from} value={form.data.from_element_id} onChange={(value) => form.setData('from_element_id', value)} options={choices} />
          <SelectControl label={copy.forms.to} value={form.data.to_element_id} onChange={(value) => form.setData('to_element_id', value)} options={choices} />
          <div className="sm:col-span-2"><SelectControl label={copy.forms.link_type} value={form.data.link_type_id} onChange={(value) => form.setData('link_type_id', value)} options={linkOptions} placeholder={copy.forms.select_link_type} /></div>
          <div className="sm:col-span-2"><TextareaControl label={copy.forms.description} value={form.data.description} onChange={(event) => form.setData('description', event.target.value)} rows={3} /></div>
          <SelectControl label={copy.forms.status} value={form.data.status} onChange={(value) => form.setData('status', value)} options={[{ value: 'proposto', label: copy.workspace.proposed }, { value: 'confirmado', label: copy.workspace.confirmed_status }]} />
          <ChangeContextFields className="sm:col-span-2" changeNote={form.data.change_note} evidenceId={form.data.evidence_id} evidences={evidences} copy={copy} onChangeNote={(value) => form.setData('change_note', value)} onEvidenceId={(value) => form.setData('evidence_id', value)} />
        </div>
    </FdeEditorDialog>
  )
}
